import { ReactNode, useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import {
  AlertTriangle,
  Boxes,
  Bell,
  Radio,
  Shield,
  GripHorizontal,
  Plus,
  RefreshCw,
  Clock,
  Activity,
  MapPin,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { cn, API_BASE_URL, SOCKET_URL, formatTimestamp, getSeverityColor, getSeverityBg, getStatusColor } from '../lib/utils';
import { canAccessCommand } from '../lib/permissions';
import { io } from 'socket.io-client';
import MapDrawingTools from '../components/map/MapDrawingTools';
import IncidentTable from '../components/shared/IncidentTable';
import StatsBar from '../components/shared/StatsBar';
import CreateIncidentModal from '../components/shared/CreateIncidentModal';
import DeclareMajorIncidentModal from '../components/shared/DeclareMajorIncidentModal';
import CommandChatPanel from '../components/shared/CommandChatPanel';
import UserDropdown from '../components/shared/UserDropdown';
import ClockDisplay from '../components/shared/ClockDisplay';
import EmergencyAlert from '../components/shared/EmergencyAlert';
import BreakRequestAlert from '../components/shared/BreakRequestAlert';
import CommandChatAlert from '../components/shared/CommandChatAlert';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { useActiveEvent } from '../contexts/EventContext';
import EventStatusModal from '../components/command/EventStatusModal';
import { requestActionLineFocus } from '../lib/actionLineFocus';
import type { Incident, DashboardStats, C2Event } from '../types';

const navItems: { path: string; icon: typeof AlertTriangle; label: string; commandOnly?: boolean }[] = [
  { path: '/workspace/incidents', icon: AlertTriangle, label: 'Incidents' },
  { path: '/workspace/resources', icon: Boxes, label: 'Resources', commandOnly: true },
  { path: '/workspace/disclosure', icon: FileText, label: 'Disclosure', commandOnly: true },
];

interface SplitOpsLayoutProps {
  children: ReactNode;
}

export default function SplitOpsLayout({ children }: SplitOpsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAppSelector((state) => state.auth);
  const { unreadCount } = useAppSelector((state) => state.notifications);
  const { activeEvent, activeEventId, setActiveEventLocal } = useActiveEvent();

  const [stats, setStats] = useState<DashboardStats>({
    activeIncidents: 0, totalIncidentsToday: 0, patientForms: 0, availableUnits: 0,
    deployedUnits: 0,
  });
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMajorIncidentModal, setShowMajorIncidentModal] = useState(false);
  const [showEventStatusModal, setShowEventStatusModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  usePushSubscription();

  const filteredNav = navItems.filter((item) => !item.commandOnly || (user && canAccessCommand(user.role)));

  const focusActionLineIfHome = useCallback(() => {
    if (location.pathname === '/workspace/incidents') {
      requestActionLineFocus();
    }
  }, [location.pathname]);

  const fetchData = useCallback(async () => {
    try {
      const incUrl = activeEventId
        ? `${API_BASE_URL}/incidents?eventId=${activeEventId}`
        : `${API_BASE_URL}/incidents`;
      const statsUrl = activeEventId
        ? `${API_BASE_URL}/dashboard/stats?eventId=${activeEventId}`
        : `${API_BASE_URL}/dashboard/stats`;
      const [statsRes, incRes] = await Promise.all([
        fetch(statsUrl, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(incUrl, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (statsRes.ok) { const d = await statsRes.json(); setStats(d.stats); }
      if (incRes.ok) { const d = await incRes.json(); setIncidents(d.incidents || d.data || []); }
    } catch { /* fallback to empty */ }
  }, [token, activeEventId]);

  // Fetch on mount and whenever we navigate back to the incidents/dashboard view
  useEffect(() => { fetchData(); }, [fetchData, location.pathname]);

  // Real-time socket: update incident list and stats on changes
  useEffect(() => {
    const token = localStorage.getItem('c2_token');
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    socket.on('incident:updated', (updated: Incident) => {
      setIncidents((prev) => prev.map((i) => i.id === updated.id ? updated : i));
      fetchData();
    });
    socket.on('incident:created', (created: Incident) => {
      setIncidents((prev) => prev.find((i) => i.id === created.id) ? prev : [created, ...prev]);
      fetchData();
    });
    socket.on('resource:updated', () => { fetchData(); });
    socket.on('resource:created', () => { fetchData(); });
    return () => { socket.disconnect(); };
  }, [fetchData]);

  // F12 keyboard shortcut to open Create Incident modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F12') {
        e.preventDefault();
        setShowCreateModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const pct = (e.clientY / window.innerHeight) * 100;
      setSplitRatio(Math.max(20, Math.min(80, pct)));
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging]);

  const isDashboard = location.pathname === '/workspace' || location.pathname === '/workspace/';
  const isIncidents = location.pathname === '/workspace/incidents';

  const handleIncidentCreated = (inc: Incident) => {
    setIncidents((prev) => [inc, ...prev]);
    fetchData();
  };

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* HORIZONTAL TOP NAV */}
      <header className="h-12 bg-card border-b border-border flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" title="Back to Command Home">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
            <Shield className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="text-sm font-bold text-foreground">C2</span>
        </button>
        {activeEvent && (
          <button
            type="button"
            onClick={() => setShowEventStatusModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
            title={`${activeEvent.name} — click to change event status (${activeEvent.status})`}
          >
            <div
              className={cn(
                'w-1.5 h-1.5 rounded-full',
                activeEvent.status === 'open' && 'bg-emerald-400 animate-pulse',
                activeEvent.status === 'supervision' && 'bg-amber-400 animate-pulse',
                activeEvent.status === 'closed' && 'bg-gray-400'
              )}
            />
            <span className="text-[10px] font-bold text-primary truncate max-w-[180px]">{activeEvent.id}</span>
            <span
              className={cn(
                'text-[8px] font-bold uppercase tracking-wide',
                activeEvent.status === 'open' && 'text-emerald-400',
                activeEvent.status === 'supervision' && 'text-amber-400',
                activeEvent.status === 'closed' && 'text-gray-400'
              )}
            >
              {activeEvent.status}
            </span>
          </button>
        )}

        <nav className="flex items-center gap-1 ml-4">
          {filteredNav.map((item) => {
            const isActive = item.path === '/workspace'
              ? location.pathname === '/workspace' || location.pathname === '/workspace/'
              : location.pathname.startsWith(item.path);
            const eventId = activeEventId || new URLSearchParams(location.search).get('eventId');
            const to = eventId ? `${item.path}?eventId=${encodeURIComponent(eventId)}` : item.path;
            return (
              <Link
                key={item.path}
                to={to}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {/* Declare Major Incident Button */}
          <button
            onClick={() => setShowMajorIncidentModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-xs font-semibold hover:bg-destructive/90 transition-colors"
            title="Declare a major incident following JESIP principles"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Major Incident
          </button>
          {/* Create Incident Button — disabled when event is closed */}
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={activeEvent?.status === 'closed'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={activeEvent?.status === 'closed' ? 'Event is closed — reopen to create incidents' : 'Create incident'}
          >
            <Plus className="w-3.5 h-3.5" />
            New Incident
          </button>
          <div className="w-px h-5 bg-border" />
          <button
            onClick={() => {
              const url = activeEventId
                ? `/workspace/map?eventId=${encodeURIComponent(activeEventId)}`
                : '/workspace/map';
              window.open(url, '_blank');
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-accent transition-colors text-xs font-medium text-muted-foreground"
            title="Open map in new window"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Pop-out Map
          </button>
          <div className="w-px h-5 bg-border" />
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-muted-foreground font-mono">Online</span>
          </div>
          <button
            onClick={() => setChatOpen(!chatOpen)}
            className={cn(
              'relative p-1.5 rounded-md hover:bg-accent transition-colors',
              chatUnread > 0 && 'bg-primary/10 ring-1 ring-primary/30'
            )}
            title="Messages"
          >
            <Radio className={cn('w-4 h-4', chatUnread > 0 ? 'text-primary' : 'text-muted-foreground')} />
            {chatUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                {chatUnread > 9 ? '9+' : chatUnread}
              </span>
            )}
          </button>
          <button className="relative p-1.5 rounded-md hover:bg-accent transition-colors">
            <Bell className="w-4 h-4 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <div className="w-px h-5 bg-border" />
          <UserDropdown />
        </div>
      </header>

      {/* STATS RIBBON */}
      <StatsBar stats={stats} layout="horizontal" />

      {/* MAIN CONTENT */}
      {isDashboard ? (
        /* ===== DASHBOARD: Full map + floating timeline + stats overlay ===== */
        <div className="flex-1 flex min-h-0 relative">
          {/* Map fills the view */}
          <div className="flex-1 relative">
            <MapDrawingTools
              incidents={incidents}
              onIncidentClick={(inc: Incident) => navigate(`/workspace/incidents/${inc.id}`)}
              className="h-full"
            />
            {/* Floating stat cards on map */}
            <div className="absolute top-3 left-3 z-[1000] flex gap-2">
              {[
                { label: 'Active', value: stats.activeIncidents, color: 'text-red-400' },
                { label: 'Today', value: stats.totalIncidentsToday, color: 'text-blue-400' },
                { label: 'Patients', value: stats.patientForms, color: 'text-rose-400' },
                { label: 'Deployed', value: stats.deployedUnits, color: 'text-cyan-400' },
                { label: 'Available', value: stats.availableUnits, color: 'text-green-400' },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-card/90 backdrop-blur border border-border rounded-lg">
                  <span className={cn('text-sm font-bold font-mono', s.color)}>{s.value}</span>
                  <span className="text-[10px] text-muted-foreground">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right-side Activity Timeline panel */}
          <div className="w-[280px] bg-card border-l border-border flex flex-col flex-shrink-0">
            <div className="px-3 py-2 border-b border-border flex items-center justify-between flex-shrink-0">
              <h2 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Activity className="w-3 h-3" /> Recent Activity
              </h2>
              <button onClick={fetchData} className="p-1 rounded hover:bg-accent transition-colors" title="Refresh">
                <RefreshCw className="w-3 h-3 text-muted-foreground" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
              {incidents.slice(0, 10).map((inc) => (
                <button
                  key={inc.id}
                  onClick={() => navigate(`/workspace/incidents/${inc.id}`)}
                  className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-mono text-muted-foreground">{inc.id}</span>
                    <span className={cn('text-[10px] px-1 py-0.5 rounded border font-bold uppercase', getSeverityBg(inc.severity), getSeverityColor(inc.severity))}>
                      {inc.severity}
                    </span>
                    <span className={cn('text-[10px] font-medium ml-auto capitalize', getStatusColor(inc.status))}>
                      {inc.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-foreground truncate">{inc.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{inc.location.address || '—'}</span>
                    <span className="flex items-center gap-0.5 ml-auto"><Clock className="w-2.5 h-2.5" />{formatTimestamp(inc.reportedAt)}</span>
                  </div>
                </button>
              ))}
              {incidents.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground text-center">No active incidents</p>
              )}
            </div>
          </div>
        </div>
      ) : isIncidents ? (
        /* ===== INCIDENTS: Map top + table bottom split with create button ===== */
        <div className="flex-1 flex flex-col min-h-0" style={{ cursor: isDragging ? 'row-resize' : undefined }}>
          <div style={{ height: `${splitRatio}%` }} className="flex-shrink-0 relative">
            <MapDrawingTools
              incidents={incidents}
              onIncidentClick={(inc: Incident) => navigate(`/workspace/incidents/${inc.id}`)}
              className="h-full"
            />
          </div>
          <div
            onMouseDown={() => setIsDragging(true)}
            className="h-1.5 bg-border hover:bg-primary/30 cursor-row-resize flex-shrink-0 flex items-center justify-center transition-colors"
          >
            <GripHorizontal className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-h-0 bg-card flex flex-col">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border flex-shrink-0">
              <h2 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Incidents ({incidents.length})
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={fetchData} className="p-1 rounded hover:bg-accent transition-colors" title="Refresh">
                  <RefreshCw className="w-3 h-3 text-muted-foreground" />
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-[10px] font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>
            </div>
            <IncidentTable
              incidents={incidents}
              onSelect={(inc) => navigate(`/workspace/incidents/${inc.id}`)}
              onOpenIncident={(id) => navigate(`/workspace/incidents/${id}`)}
              className="flex-1 min-h-0"
            />
          </div>
        </div>
      ) : (
        <main className="flex-1 overflow-hidden p-4">{children}</main>
      )}

      {/* STATUS BAR */}
      <footer className="h-7 border-t border-border bg-card flex items-center px-4 text-[10px] text-muted-foreground flex-shrink-0">
        <span className="font-mono">C2 v1.0.0</span>
        <span className="mx-2 w-px h-3 bg-border" />
        <span className="flex items-center gap-1">
          <Radio className="w-2.5 h-2.5 text-green-400 animate-pulse" /> Connected
        </span>
        <span className="mx-2 w-px h-3 bg-border" />
        <span>Split Operations View</span>
      </footer>

      {/* CLOCK DISPLAY - Bottom Right */}
      <ClockDisplay />

      {/* CREATE INCIDENT MODAL */}
      <CreateIncidentModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          focusActionLineIfHome();
        }}
        onCreated={handleIncidentCreated}
      />

      {/* DECLARE MAJOR INCIDENT MODAL */}
      <DeclareMajorIncidentModal
        open={showMajorIncidentModal}
        onClose={() => setShowMajorIncidentModal(false)}
        onCreated={(maj) => {
          setShowMajorIncidentModal(false);
          navigate(`/workspace/major-incidents/${maj.id}`);
        }}
      />

      {/* CHAT PANEL */}
      <CommandChatPanel
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          focusActionLineIfHome();
        }}
        onUnreadChange={setChatUnread}
      />

      {/* EMERGENCY ALERT */}
      <EmergencyAlert />
      <BreakRequestAlert />
      <CommandChatAlert chatOpen={chatOpen} onOpenChat={() => setChatOpen(true)} />

      {activeEvent && (
        <EventStatusModal
          open={showEventStatusModal}
          event={activeEvent}
          onClose={() => {
            setShowEventStatusModal(false);
            focusActionLineIfHome();
          }}
          onUpdated={(updated: C2Event) => {
            setActiveEventLocal(updated);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
